import { useState, useEffect } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { TimeInput } from './TimeInput';
import { StartsAtStopsAt } from './StartsAtStopsAt';
import { useLockBodyScroll } from './useLockBodyScroll';

const animatedComponents = makeAnimated();

export default function MonthlyEventModal({
  event,
  langUrl,
  isOpen,
  onClose,
  onSave,
  onDelete,
  demoMode = false,
  trads,
  monthName,
}) {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [time, setTime] = useState(undefined);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [startsAt, setStartsAt] = useState(null);
  const [stopsAt, setStopsAt] = useState(null);

  const t = trads?.index?.eventModal || {};
  const tContacts = trads?.contacts || {};
  const modalTitle = event?.id
    ? t.editTitle || t.title || 'Edit event'
    : t.createTitle || 'Add event';

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (event) {
      setName(event.name || '');
      setContent(event.content || '');
      setStartsAt(event.startsAt || null);
      setStopsAt(event.stopsAt || null);
      setSelectedGroupIds(event.groupIds || []);
      const selected = (event.eventContacts || []).map((ec) => ({
        value: ec.contactId || ec.contact?.id,
        label: ec.contact?.name || ec.name,
        verified: ec.contact?.verified ?? ec.verified,
        phoneNumber: ec.contact?.phoneNumber,
      }));
      setSelectedContacts(selected);
    }
  }, [event]);

  useEffect(() => {
    if (isOpen && !demoMode) {
      setLoadingContacts(true);
      fetch('/api/contacts/get-contacts')
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.contacts) {
            setContacts(data.contacts);
            setGroups(data.groups || []);
            if (data.contacts.length === 1) {
              setSelectedContacts((prev) =>
                prev.length === 0
                  ? [
                      {
                        value: data.contacts[0].id,
                        label: data.contacts[0].name,
                      },
                    ]
                  : prev
              );
            }
          }
        })
        .catch((err) => console.error('Failed to fetch contacts:', err))
        .finally(() => setLoadingContacts(false));
    }
  }, [isOpen, demoMode]);

  const contactOptions = contacts.map((c) => ({
    value: c.id,
    label: c.name,
  }));
  const groupOptions = groups.map((group) => ({
    value: group.id,
    label: `${group.name} (${group.count})`,
  }));
  const selectedGroupOptions = groupOptions.filter((group) =>
    selectedGroupIds.includes(group.value)
  );

  if (!isOpen || !event) return null;

  const handleSave = () => {
    onSave?.({
      ...event,
      name,
      content,
      startsAt,
      stopsAt,
      daysInMonth: (event.daysInMonth || []).map(Number),
      times: time ? [timeStringToMinutes(time)] : event.times,
      eventContacts: selectedContacts.map((sc) => ({ contactId: sc.value })),
      groupIds: selectedGroupIds,
    });
    onClose();
  };

  const handleDelete = () => {
    if (onDelete && event.id) {
      onDelete(event.id);
      onClose();
    }
  };

  const currentDay = event?.daysInMonth?.[0] || '';
  const getOrdinalLabel = (day) => {
    const remainder10 = day % 10;
    const remainder100 = day % 100;

    if (remainder10 === 1 && remainder100 !== 11) return `${day}st`;
    if (remainder10 === 2 && remainder100 !== 12) return `${day}nd`;
    if (remainder10 === 3 && remainder100 !== 13) return `${day}rd`;

    return `${day}th`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {modalTitle}{' '}
            {currentDay === 32
              ? `- ${trads?.index?.lastDayOfTheMonth || 'Last day of the month'}`
              : currentDay
                ? `- ${getOrdinalLabel(currentDay)} of each month`
                : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <input
              type="text"
              value={name}
              placeholder={t.name || 'Event name'}
              onChange={(e) => setName(e.target.value)}
              data-testid="monthly-event-name-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.smsContent || 'SMS text'}
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              data-testid="monthly-event-content-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.time || 'Time'}
            </label>
            <TimeInput
              trads={trads}
              onChange={setTime}
              defaultValue={event.times?.[0]}
            />
          </div>

          <StartsAtStopsAt
            startsAt={startsAt}
            stopsAt={stopsAt}
            setStartsAt={setStartsAt}
            setStopsAt={setStopsAt}
            trads={trads}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
              {t.contacts || 'Contacts'}
            </label>
            {demoMode ? (
              <div>
                {selectedContacts.length > 0 ? (
                  <div className="space-y-2">
                    {selectedContacts.map((contact) => (
                      <div
                        key={contact.value}
                        className="px-3 py-2 bg-green-50 border border-green-200 rounded-md text-green-800"
                      >
                        <div className="font-medium">{contact.label}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-500 text-sm">
                    No contacts selected
                  </div>
                )}
              </div>
            ) : (
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={contactOptions}
                value={selectedContacts}
                onChange={setSelectedContacts}
                isLoading={loadingContacts}
                placeholder={t.selectContacts || 'Select contacts...'}
                noOptionsMessage={() => tContacts.noContacts || 'No contacts'}
                classNamePrefix="contacts-select"
              />
            )}
          </div>

          {!demoMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
                {t.groups || 'Groups'}
              </label>
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={groupOptions}
                value={selectedGroupOptions}
                onChange={(values) =>
                  setSelectedGroupIds(
                    (values || []).map((group) => group.value)
                  )
                }
                placeholder={t.selectGroups || 'Select groups...'}
                noOptionsMessage={() => tContacts.noGroups || 'No groups'}
                data-testid="monthly-groups-select"
                classNamePrefix="contacts-select"
              />
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-between gap-3">
          {onDelete && event.id ? (
            <button
              type="button"
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              {tContacts.delete || 'Delete'}
            </button>
          ) : (
            <div></div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              {t.cancel || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={content.trim().length < 3}
              data-testid="monthly-event-save-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.save || 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function timeStringToMinutes(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}
